import "./grid-item.scss";

import { GetCampaignModel } from "@factory/model/query-model-campaign";
import { GetRewardCampaignModel } from "@factory/model/query-model-reward";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import useScreenSize from "@shared/utility/screen-size";
import { toBoolean } from "@shared/utility/to-boolean";
import { Button } from "primereact/button";
import { useCallback } from "react";

import { appFeature } from "../../../app.feature";
import TagColor from "../tag-color/tag-color";

export class GridItemModel {
  image: string;
  name: string;
  description: string;
  points: number;
  stamp: number;
  expirationDate: string;
  constraint: string;
  code: string;
  metadata?: object;

  constructor(
    image: string,
    name: string,
    description: string,
    points: number,
    stamp: number,
    expirationDate: string,
    constraint: string,
    code: string,
    metadata?: object
  ) {
    this.image = image;
    this.name = name;
    this.description = description;
    this.points = points;
    this.stamp = stamp;
    this.expirationDate = expirationDate;
    this.constraint = constraint;
    this.code = code;
    this.metadata = metadata;
  }

  static ConvertByRewardCampaign(model: GetRewardCampaignModel) {
    return new GridItemModel(
      model?.image ?? "",
      model?.name ?? "",
      model?.description ?? "",
      model?.points ?? 0,
      model?.stamp ?? 0,
      model?.end_date ?? "",
      model?.constraint ?? "",
      model?.code ?? "",
      model
    );
  }

  static ConvertByCampaign(model: GetCampaignModel) {
    return new GridItemModel(
      model.image || "",
      model.name || "",
      model.description || "",
      model.points || 0,
      model.stamp || 0,
      model.end_date || "",
      model.constraint || "",
      model.code || "",
      model
    );
  }
}

export interface GridItemProps {
  campaign: GridItemModel;
  shouldHideCriteria?: boolean;
  onRedeem?: (item: GridItemModel) => void;
}

export function GridItem({
  campaign,
  shouldHideCriteria,
  onRedeem,
}: GridItemProps) {
  // DECLARATIONS
  const { pointsText, stampsText } = appFeature.config.label;
  const { formatDateLocal } = useDateTimeContext();
  const { isMobileMode } = useScreenSize();

  // API DEFINITIONS
  // LOCAL FUNCTIONS
  // LOCAL RENDERS
  const redeemIcon = useCallback(() => {
    const campaignData = campaign?.metadata;
    let redeemIcon = "pi pi-gift";
    if (campaignData && "is_redeemable" in campaignData) {
      redeemIcon = toBoolean(campaignData.is_redeemable as string)
        ? "pi pi-gift"
        : "pi pi-eye";
    }

    return redeemIcon;
  }, [campaign]);

  return (
    <div id="GridItem" className="grid-item">
      <div
        className="flex shadow-md rounded-md overflow-hidden border h-44"
        onClick={(e) => {
          e.preventDefault();

          if (isMobileMode && onRedeem) {
            onRedeem(campaign);
          }
        }}
      >
        <img
          src={campaign?.image}
          className="h-full w-28 object-cover object-center"
        />

        <div className="flex flex-col w-full">
          <div className="flex justify-between border border-dashed border-gray-300 mx-4 items-center p-1 mt-2">
            {shouldHideCriteria ? null : (
              <div className="flex gap-2 items-center justify-center">
                <span>Criteria</span>
                <TagColor value={campaign.constraint} />
              </div>
            )}
            <p className="font-bold">
              {campaign?.points} {pointsText}
            </p>
            <p className="font-bold">
              {campaign?.stamp} {stampsText}
            </p>
          </div>

          <section className="flex">
            <div className="pl-4 py-1 flex-grow flex justify-start flex-col items-start">
              <h4>{campaign?.name}</h4>
              <p className="text-gray-400">
                Expires on{" "}
                {campaign.expirationDate
                  ? formatDateLocal(campaign.expirationDate)
                  : null}
              </p>

              <p className="text-ellipsis truncate w-32">
                {campaign?.description}
              </p>
            </div>

            {onRedeem && !isMobileMode ? (
              <div className="flex items-center justify-center mx-4">
                <Button
                  icon={redeemIcon()}
                  outlined
                  onClick={() => onRedeem(campaign)}
                />
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}

export default GridItem;
